import React, { lazy, Suspense, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { Login } from '@/pages/Login';
const Weather = lazy(() => import('@/pages/Weather').then(m => ({ default: m.Weather })));
const Pricing = lazy(() => import('@/pages/Pricing').then(m => ({ default: m.Pricing })));
const Spreads = lazy(() => import('@/pages/Spreads'));
const Fundamentals = lazy(() => import('@/pages/Fundamentals').then(m => ({ default: m.Fundamentals })));
const FlowMap = lazy(() => import('@/pages/FlowMap').then(m => ({ default: m.FlowMap })));
// Balance is a sub-tab of Models (no standalone page)
const Models = lazy(() => import('@/pages/Models').then(m => ({ default: m.Models })));
const CronJobs = lazy(() => import('@/pages/CronJobs').then(m => ({ default: m.CronJobs })));
const DBExplorer = lazy(() => import('@/pages/DBExplorer').then(m => ({ default: m.DBExplorer })));
const Status = lazy(() => import('@/pages/Status').then(m => ({ default: m.Status })));
const SystemHealth = lazy(() => import('@/pages/SystemHealth').then(m => ({ default: m.SystemHealth })));
import { AuthCtx, useAuthProvider } from '@/hooks/useAuth';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';

const PUBLIC_PATHS = ['/status'];

function AuthGuard({ children }: { children: React.ReactNode }) {
  const auth = React.useContext(AuthCtx);
  const location = useLocation();
  if (auth.isLoading && !PUBLIC_PATHS.includes(location.pathname)) return null;
  if (!auth.isAuthed && !PUBLIC_PATHS.includes(location.pathname)) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function DashboardLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { sidebarHidden } = useBreakpoint();

  // Close mobile menu on route change
  const location = useLocation();
  React.useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[var(--bg-base)]">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />

      {/* Mobile drawer overlay */}
      {sidebarHidden && mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed left-0 top-0 bottom-0 w-64 z-50 bg-[var(--bg-base)] border-r border-[var(--border-subtle)] overflow-y-auto">
            <Sidebar collapsed={false} onToggle={() => setMobileMenuOpen(false)} forceShow />
          </div>
        </>
      )}

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <TopBar
          title="Caladan Capital"
          showMenuButton={sidebarHidden}
          onMenuToggle={() => setMobileMenuOpen(o => !o)}
        />
        <main className="flex-1 overflow-y-auto p-3 sm:p-[var(--space-6)]">
          <ErrorBoundary>
            <Suspense fallback={<div role="status" className="p-6 text-[var(--text-secondary)]">Loading page…</div>}>
            <Routes>
              <Route path="weather" element={<Weather />} />
              <Route path="fundamentals" element={<Fundamentals />} />
              <Route path="flow-map" element={<FlowMap />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="spreads" element={<Spreads />} />
              <Route path="models" element={<Models />} />
              <Route path="cron" element={<CronJobs />} />
              <Route path="db-explorer" element={<DBExplorer />} />
              {/* Balance is under /models, not standalone */}
              <Route path="health" element={<SystemHealth />} />
              <Route path="status" element={<Status />} />
              <Route path="*" element={<Navigate to="weather" replace />} />
            </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  const auth = useAuthProvider();
  return (
    <AuthCtx.Provider value={auth}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/*" element={<AuthGuard><DashboardLayout /></AuthGuard>} />
      </Routes>
    </AuthCtx.Provider>
  );
}
